import type {
  DeactivatedProfilesStatsResponse,
  ExclusionKeyword,
  ProfileResponse,
  RestoreProfilesResponse,
  ScanExclusionsResponse,
  ScanPreviewResponse,
} from '@shared/schemas';

import api from '@/shared/api/api-client';

import { type DeactivatedFilters, resolveDateRange } from '../domain/deactivated-filters';
import type { DeactivatedProfilesGateway } from './deactivated-profiles.gateway';

/**
 * The toolbar's filters as query parameters.
 *
 * Empty values are OMITTED rather than sent blank: the endpoint reads "absent"
 * as "no constraint", and `reason=` would be a value it has to defend against.
 * The date preset is resolved here, at fetch time; a page left open overnight
 * asks for the right seven days the next morning.
 */
function appendFilters(params: URLSearchParams, filters: DeactivatedFilters | undefined): void {
  if (!filters) return;

  const search = filters.search.trim();
  if (search) params.set('q', search);
  if (filters.method) params.set('reason', filters.method);
  if (filters.keyword) params.set('keyword', filters.keyword);
  if (filters.type) params.set('type', filters.type);

  const { from, to } = resolveDateRange(filters.range);
  if (from) params.set('from', from.toISOString());
  if (to) params.set('to', to.toISOString());
}

/**
 * Real implementation over the shared api client: the single place that knows the
 * Deactivated Profiles endpoints, kept 1:1 with the original page. Path building
 * mirrors `useInfiniteApi('/profiles/deactivated', 25)` (1-indexed server page).
 */
export const httpDeactivatedProfilesGateway: DeactivatedProfilesGateway = {
  fetchDeactivated: async ({ page, pageSize, filters }) => {
    const params = new URLSearchParams({
      page: String(page + 1), // backend is 1-indexed
      limit: String(pageSize),
    });
    appendFilters(params, filters);
    const res = await api.getRaw<ProfileResponse[]>(`/profiles/deactivated?${params}`);
    const rows = res.data ?? [];
    return { rows, total: res.pagination?.total ?? rows.length };
  },

  restoreProfiles: (ids) =>
    api.post<RestoreProfilesResponse>('/profiles/restore', {
      profile_ids: ids,
    }),

  fetchReactivated: async ({ page, pageSize }) => {
    const serverPage = page + 1; // backend is 1-indexed
    const res = await api.getRaw<ProfileResponse[]>(
      `/profiles/reactivated?page=${serverPage}&limit=${pageSize}`,
    );
    const rows = res.data ?? [];
    return { rows, total: res.pagination?.total ?? rows.length };
  },

  fetchStats: () => api.get<DeactivatedProfilesStatsResponse>('/profiles/deactivated/stats'),

  fetchExclusionKeywords: () => api.get<ExclusionKeyword[]>('/exclusion-keywords'),

  addExclusionKeyword: (word) => api.post<ExclusionKeyword>('/exclusion-keywords', { word }),

  deleteExclusionKeyword: async (id) => {
    await api.delete(`/exclusion-keywords/${id}`);
  },

  scanExclusions: () => api.post<ScanExclusionsResponse>('/exclusion-keywords/scan'),

  previewScan: () => api.post<ScanPreviewResponse>('/exclusion-keywords/scan/preview'),

  fetchProfileTypes: async () => {
    const rows = await api.get<{ name: string }[]>('/profile-types');
    return rows.map((r) => r.name);
  },
};
